import { backend_api_v2 } from '/src/utils/apiv2'
import { defineStore } from 'pinia'

// Sibling of useRecordsStore. Holds RCPs and checklist packages for every
// member of a MultipanelGroup so the multiwall checklist UI can read/update
// many proofs at once without disturbing the singleton `rcp` used by the
// per-panel viewer.
//
// Per-member queues mirror the queue logic of useRecordsStore.flushChecklistItems
// so concurrent posts to the same member serialize -- and batch into one
// `post_chis` -- while posts to different members run in parallel.
export const useMultipanelRecordsStore = defineStore('multipanel_records', {
    state: () => ({
        rcps: {},        // member_uuid -> RCP
        pcks: {},        // member_uuid -> LinkedFilePackage (checklist)
        loading: false,
        loading_pcks: false,
        chi_queues: {},      // member_uuid -> [items]
        chi_flushes: {},     // member_uuid -> in-flight drain promise
    }),
    getters: {
        loadedMemberUuids: (state) => Object.keys(state.rcps).filter(u => state.rcps[u]),
    },
    actions: {
        clearAll() {
            this.rcps = {}
            this.pcks = {}
            this.chi_queues = {}
            this.chi_flushes = {}
        },
        setRCP(member_uuid, rcp) { this.rcps = { ...this.rcps, [member_uuid]: rcp } },
        setPck(member_uuid, pck) { this.pcks = { ...this.pcks, [member_uuid]: pck } },

        async loadOne(member_uuid) {
            try {
                const { data } = await backend_api_v2.get(`records/rcps/?assembly=${member_uuid}`)
                if (data.results.length > 0) {
                    this.setRCP(member_uuid, data.results[0])
                } else {
                    this.setRCP(member_uuid, null)
                }
            } catch (e) {
                console.log(`Cant load multipanel RCP for ${member_uuid}: ${e}`)
                this.setRCP(member_uuid, null)
            }
        },
        async loadAll(member_uuids) {
            this.loading = true
            try {
                await Promise.all(member_uuids.map(u => this.loadOne(u)))
            } finally {
                this.loading = false
            }
        },
        async createOne(member_uuid) {
            // Seeds an RCP for an assembly that doesn't have one yet.
            // Mirrors useRecordsStore.createRCP — assembly-level only since
            // multipanel members are always assemblies.
            try {
                const r = await backend_api_v2.get(
                    `constructibles/assemblies/${member_uuid}/create_proof/`
                )
                if ([200, 201].includes(r.status)) {
                    await this.loadOne(member_uuid)
                }
            } catch (e) {
                console.log(`Cant create proof for member ${member_uuid}: ${e}`)
            }
        },
        async startStationForMembers(member_uuids, station_name, production_line_name) {
            // Ensures every targeted member has an RCP and that the given
            // station is marked STATION_STARTED on each. Used by the multiwall
            // "Start panels" affordance.
            this.loading = true
            try {
                // 1) Create proofs for any missing RCPs and load their packages.
                const missing = member_uuids.filter(u => !this.rcps[u])
                if (missing.length > 0) {
                    await Promise.all(missing.map(u => this.createOne(u)))
                    await Promise.all(missing.map(u => this.loadPackage(u)))
                }
                // 2) For every member, post STATION_STARTED if not already there
                //    for this (station, line) tuple.
                const targets = member_uuids.filter(u => this.rcps[u])
                await Promise.all(targets.map(u => {
                    const rcp = this.rcps[u]
                    const already = (rcp.checklist || []).some(it =>
                        it.text === "STATION_STARTED"
                        && it.production_station === station_name
                        && it.production_line === production_line_name
                    )
                    if (already) return Promise.resolve()
                    return this.postChecklistItem(u, {
                        text: "STATION_STARTED",
                        production_line: production_line_name,
                        production_station: station_name,
                        checked: true,
                        timestamp: new Date(),
                    })
                }))
            } finally {
                this.loading = false
            }
        },
        async loadPackage(member_uuid) {
            const rcp = this.rcps[member_uuid]
            if (!rcp) return
            try {
                const { data } = await backend_api_v2.post('/cloudstorage/packages/get_or_create/', {
                    name: 'checklist',
                    proof: rcp.uuid,
                })
                this.setPck(member_uuid, data)
            } catch (e) {
                console.log(`Cant load multipanel checklist package for ${member_uuid}: ${e}`)
            }
        },
        async loadPackages(member_uuids) {
            this.loading_pcks = true
            try {
                await Promise.all(
                    member_uuids
                        .filter(u => this.rcps[u])
                        .map(u => this.loadPackage(u))
                )
            } finally {
                this.loading_pcks = false
            }
        },

        async postChecklistItem(member_uuid, item) {
            if (!this.chi_queues[member_uuid]) this.chi_queues[member_uuid] = []
            this.chi_queues[member_uuid].push(item)
            return this.flushChecklistItems(member_uuid)
        },

        /** Per-member sibling of `useRecordsStore.flushChecklistItems`.
         *
         * Same reasoning, same shape: one `post_chis` per member instead of one
         * `post_chi` per item, and one shared promise per member instead of a
         * polled flag. It matters more here than in the single-panel store,
         * because the multipanel flow multiplies by the member count — marking
         * a station on `M` panels was `M × (2N + 1)` requests and is now `M`.
         *
         * The promise is per member on purpose: posts to the same proof must
         * serialize, posts to different proofs should not.
         */
        async flushChecklistItems(member_uuid) {
            if (this.chi_flushes[member_uuid]) return this.chi_flushes[member_uuid]
            this.chi_flushes[member_uuid] = (async () => {
                try {
                    while (this.chi_queues[member_uuid]?.length > 0) {
                        const rcp = this.rcps[member_uuid]
                        if (!rcp) {
                            // No proof to post to. Drop what was queued rather
                            // than spinning on a queue that can never drain.
                            this.chi_queues[member_uuid].splice(0)
                            break
                        }
                        const items = this.chi_queues[member_uuid].splice(0)
                        const { data } = await backend_api_v2.post(
                            `records/rcps/${rcp.uuid}/post_chis/`,
                            items
                        )
                        this.setRCP(member_uuid, data)
                    }
                } catch (error) {
                    this.chi_queues[member_uuid]?.splice(0)
                    console.error('Error processing multipanel checklist items:', error)
                } finally {
                    delete this.chi_flushes[member_uuid]
                }
            })()
            return this.chi_flushes[member_uuid]
        },
        async postChecklistItemForMembers(member_uuids, item) {
            return Promise.all(
                member_uuids.map(u => this.postChecklistItem(u, item))
            )
        },
        async setStationReadyForMembers(
            member_uuids,
            station_name,
            production_line_name,
            mark_proof_ready = false,
            prev_stations = [],
        ) {
            // Marks the given station as ready (STATION_JOB_READY) on every
            // targeted member. Any `prev_stations` (stations earlier in the
            // production line) that aren't yet STARTED/READY are auto-marked
            // first, mirroring the per-panel setPrevsReadyAndStartStation
            // flow — so finishing a station downstream implicitly closes any
            // upstream stations that the operator skipped.
            //
            // If `mark_proof_ready` is true (last station of the production
            // line) the RCP itself is also flagged with ready_date.
            this.loading = true
            try {
                await Promise.all(member_uuids.map(async u => {
                    const rcp = this.rcps[u]
                    if (!rcp) return

                    const has_marker = (text, sta_name) => {
                        const cl = this.rcps[u]?.checklist || []
                        return cl.some(t =>
                            t.text === text
                            && t.production_line === production_line_name
                            && t.production_station === sta_name
                        )
                    }

                    // 1) Backfill prev stations as STARTED + READY where missing.
                    for (const prev of prev_stations) {
                        if (!has_marker("STATION_STARTED", prev.name)) {
                            await this.postChecklistItem(u, {
                                text: "STATION_STARTED",
                                production_line: production_line_name,
                                production_station: prev.name,
                                checked: true,
                                timestamp: new Date(),
                            })
                        }
                        if (!has_marker("STATION_JOB_READY", prev.name)) {
                            await this.postChecklistItem(u, {
                                text: "STATION_JOB_READY",
                                production_line: production_line_name,
                                production_station: prev.name,
                                checked: true,
                                timestamp: new Date(),
                            })
                        }
                    }

                    // 2) Mark the target station ready.
                    await this.postChecklistItem(u, {
                        text: "STATION_JOB_READY",
                        production_line: production_line_name,
                        production_station: station_name,
                        checked: true,
                        timestamp: new Date(),
                    })

                    // 3) Last station of the line → flag the proof itself.
                    if (mark_proof_ready) {
                        const updated = this.rcps[u]
                        if (!updated) return
                        try {
                            const next = { ...updated, ready_date: new Date() }
                            const { data } = await backend_api_v2.put(
                                `records/rcps/${updated.uuid}/`,
                                next
                            )
                            this.setRCP(u, data)
                        } catch (e) {
                            console.error(`Cant mark RCP ready for ${u}:`, e)
                        }
                    }
                }))
            } finally {
                this.loading = false
            }
        },
    },
})
